import React, { useContext, useRef, useState, useEffect } from "react";
import DOMPurify from "dompurify";
import authService from "../services/auth.service";
import apiService from "../services/api";
import { useLocation, useNavigate } from "react-router-dom";
import { AppContext, AppContextType, AppProviderProps, Message, User } from "../types";

const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const lastMsg = useRef<HTMLDivElement>(null);
  const [messageText, setMessageText] = useState<string>("");
  const [processing, setProcessing] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [activeChat, setActiveChat] = useState<any>(null);
  const [chats, setChats] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.log(isAuthenticated)
  }, [isAuthenticated]);

  // Fetch chats when user logs in
  useEffect(() => {
    const fetchChats = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        setError(null);
        const userChats = await apiService.fetchHistory(user.role,user.username);
        setChats(userChats);
      } catch (err) {
        setError("Failed to load chats");
        console.error("Error fetching chats:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChats();
  }, [user]);

  // Get current active chat messages
  const activeMessages = activeChat 
    ? chats.find((chat: any) => chat.session_id === activeChat)?.messages || []
    : [];

  const handleChatSelect = (chatId: number) => {
    setActiveChat(chatId);
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      
      const userData = await authService.login({ email, password });
      setUser(userData);
      setIsAuthenticated(true);
      
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (userData.role === "doctor") {
        navigate("/doctor");
      } else if (userData.role === "sales") {
        navigate("/sales");
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setActiveChat(null);
    setChats([]);
  };

  const handleSubmission = async (): Promise<void> => {
    if (!messageText.trim() || processing) return;

    const sanitizedMessageText = DOMPurify.sanitize(messageText);

    const userMessage: Message = {
      role: "human",
      text: sanitizedMessageText,
    };

    // Generate a new session ID if there's no active chat
    const sessionId = activeChat || crypto.randomUUID();
    
    const data = {
      text: sanitizedMessageText,
      user_id: user?.role || "",
      session_id: sessionId,
      user_name: user?.username || "",
    };

    // If this is a new chat, create a new chat object
    if (!activeChat) {
      const newChat = {
        session_id: sessionId,
        messages: [],
        last_updated: new Date().toISOString() 
      };
      setChats((prevChats:any) => [newChat,...prevChats]);
      setActiveChat(sessionId);
    }

    // Update messages in the active chat
    setChats((prevChats: any) => prevChats.map((chat: any) => {
      if (chat.session_id === sessionId) {
        return {
          ...chat,
          messages: [...chat.messages, userMessage],
          last_updated: new Date().toISOString() 
        };
      }
      return chat;
    }));

    setMessageText("");
    setProcessing(true);

    const endpoint = location.pathname === "/sales" ? "/sales" : "/doctor";

    try {
      const response = await apiService.sendMessage(endpoint, data);
      
      // const updatedResponse = {
      //   ...response,
      //   role: response.role || 'ai', // Set default role to 'ai' if it's missing
      // };
      
      // Update chats with AI response
      setChats((prevChats: any) => prevChats.map((chat: any) => {
        if (chat.session_id === sessionId) {
          return {
            ...chat,
            messages: [...chat.messages, response],
            last_updated: new Date().toISOString() 
          };
        }
        return chat;
      }));
    } catch (err) {
      const errorMessage: Message = {
        role: "ai",
        text: "Error Processing this message. Please try again later.",
        timestamp: new Date().toISOString()
      };
      
      setChats((prevChats: any) => prevChats.map((chat: any) => {
        if (chat.session_id === sessionId) {
          return {
            ...chat,
            messages: [...chat.messages, errorMessage],
            last_updated: new Date().toISOString() 
          };
        }
        return chat;
      }));
    } finally {
      setProcessing(false);
      setTimeout(() =>
        lastMsg.current?.scrollIntoView({
          behavior: "smooth",
        })
      );
    }
  };

  const handleRegenerate = async (): Promise<void> => {
    
    if (!activeChat || processing) return;
  
    const activeMessages = chats.find((chat: any) => chat.session_id === activeChat)?.messages || [];
    const lastUserMessage = activeMessages.findLast((msg: any) => msg.role === "user");

  
    if (!lastUserMessage) return;
  
    setProcessing(true);
  
    const data = {
      text: lastUserMessage.message,
      user_id: user?.role || "",
      session_id: activeChat,
      user_name: user?.username || "",
    };
  
    const endpoint = location.pathname === "/sales" ? "/sales" : "/doctor";
  
    try {
      const response = await apiService.sendMessage(endpoint, data);
  
      // Replace the last AI message with the new response
      setChats((prevChats: any) =>
        prevChats.map((chat: any) => {
          if (chat.session_id === activeChat) {
            const updatedMessages = chat.messages.slice(0, -1); // Remove the last AI message
            return {
              ...chat,
              messages: [...updatedMessages, response], // Add the new AI response
              last_updated: new Date().toISOString(),
            };
          }
          return chat;
        })
      );
    } catch (err) {
      const errorMessage: Message = {
        role: "ai",
        text: "Error Processing this message. Please try again later.",
        timestamp: new Date().toISOString(),
      };
  
      setChats((prevChats: any) =>
        prevChats.map((chat: any) => {
          if (chat.session_id === activeChat) {
            const updatedMessages = chat.messages.slice(0, -1); // Remove the last AI message
            return {
              ...chat,
              messages: [...updatedMessages, errorMessage], // Add the error message
              last_updated: new Date().toISOString(),
            };
          }
          return chat;
        })
      );
    } finally {
      setProcessing(false);
      setTimeout(() =>
        lastMsg.current?.scrollIntoView({
          behavior: "smooth",
        })
      );
    }
  };

  return (
    <AppContext.Provider
      value={{
        lastMsg,
        messageText,
        setMessageText,
        processing,
        setProcessing,
        messages: activeMessages,
        user,
        setUser,
        isAuthenticated,
        login,
        logout,
        activeChat,
        handleSubmission,
        handleRegenerate, 
        setActiveChat: handleChatSelect,
        chats,
        setChats,
        isLoading,
        error
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;

export const useGlobalContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useGlobalContext must be used within an AppProvider");
  }
  return context;
};